import { API_BASE } from './apiConfig'

interface FetchOptions extends RequestInit {
  json?: unknown
  noAutoRedirect?: boolean
}

/**
 * Fetch wrapper with credentials:'include' (httpOnly cookie support).
 * On 401: dispatches 'auth:unauthorized' event for AuthContext to handle
 * unless noAutoRedirect is true (used for auth checks and login itself).
 *
 * Relative URLs (starting with /) are prefixed with API_BASE so they
 * reach the correct Workers backend in production.
 */
export async function authFetch(url: string, options: FetchOptions = {}): Promise<Response> {
  const { json, noAutoRedirect = false, headers: optHeaders, body, ...rest } = options

  const headers = new Headers(optHeaders as HeadersInit | undefined)
  if (json !== undefined) {
    headers.set('Content-Type', 'application/json')
  }

  // Prefix relative URLs with API_BASE (empty in dev, full URL in prod)
  const fullUrl = url.startsWith('/') ? `${API_BASE}${url}` : url

  const response = await fetch(fullUrl, {
    ...rest,
    headers,
    credentials: 'include',
    body: json !== undefined ? JSON.stringify(json) : body,
  })

  if (response.status === 401 && !noAutoRedirect) {
    window.dispatchEvent(new Event('auth:unauthorized'))
  }

  return response
}

export interface ApiErrorWithDetails {
  error: string
  details?: {
    fieldErrors?: Record<string, string[]>
    [key: string]: unknown
  }
  [key: string]: unknown
}

/**
 * Like authFetch but parses JSON and throws on non-OK responses.
 * Preserves full error response (including field-level errors) as error.response property.
 */
export async function authFetchJson<T>(url: string, options: FetchOptions = {}): Promise<T> {
  const response = await authFetch(url, options)
  if (!response.ok) {
    let message: string
    let errorResponse: ApiErrorWithDetails | undefined
    try {
      const data = await response.json() as ApiErrorWithDetails
      errorResponse = data
      message =
        (data['error'] as string | undefined) ??
        (data['message'] as string | undefined) ??
        `HTTP ${response.status}`
    } catch {
      message = `HTTP ${response.status}`
    }
    const error = new Error(message) as Error & { response?: ApiErrorWithDetails }
    error.response = errorResponse
    throw error
  }
  return response.json() as Promise<T>
}
